import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ExternalLink, FileText, ImageIcon, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, API, getToken } from "@/lib/api";
import { fmtDate } from "@/lib/format";

/**
 * Phase 142 — "New drawing issued" banner for trades on site.
 * Lists every re-issued drawing the viewer can see but hasn't opened since it landed. Opening one (here, from the
 * documents list, or any download) records the view server-side and the entry disappears. No dismiss — the whole
 * point is that nobody builds off a superseded sheet without having at least opened the current one.
 *
 *   jobId       limit to one job (job page); omit for every job the viewer is on (dashboard)
 *   onOpenFile  optional — called with the file id instead of opening a new tab (job page selects it in the preview)
 *   refreshKey  bump to re-fetch (e.g. after the viewer opened something)
 */
export const DrawingAlertsBanner = ({ jobId = "", onOpenFile, refreshKey = 0, compact = false }) => {
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [hiddenThisSession, setHiddenThisSession] = useState(false);

  const load = useCallback(() => {
    api
      .get("/drawing-alerts", { params: jobId ? { job_id: jobId } : {} })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const markOpened = async (item) => {
    // optimistic — the server also records the view when the file downloads
    setItems((list) => (list || []).filter((x) => x.file_id !== item.file_id));
    try {
      await api.post(`/files/${item.file_id}/opened`);
    } catch {
      /* the download itself records the view; nothing to surface */
    }
  };

  const open = (item) => {
    if (onOpenFile) onOpenFile(item.file_id);
    else window.open(`${API}/files/${item.file_id}/download?token=${encodeURIComponent(getToken() || "")}`, "_blank", "noopener");
    markOpened(item);
  };

  if (!items || items.length === 0 || hiddenThisSession) return null;
  const visible = expanded ? items : items.slice(0, 3);
  const more = items.length - visible.length;

  return (
    <section
      role="status"
      aria-live="polite"
      className={`rounded-xl border border-amber-500/50 bg-amber-500/10 ${compact ? "p-3" : "p-3 sm:p-4"}`}
      data-testid="drawing-alerts-banner"
      data-count={items.length}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/20 text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-100" data-testid="drawing-alerts-title">
              New drawing{items.length === 1 ? "" : "s"} issued
            </p>
            <Badge variant="outline" className="h-5 border-amber-500/40 bg-amber-500/15 px-1.5 text-[10px] font-semibold text-amber-800 dark:text-amber-200" data-testid="drawing-alerts-count">
              {items.length}
            </Badge>
          </div>
          <p className="mt-0.5 text-xs text-amber-900/80 dark:text-amber-100/80">
            Earlier sheets are superseded — open each current issue before you build off it. This notice clears once you've opened them.
          </p>
          <ul className="mt-2 space-y-1.5" data-testid="drawing-alerts-list">
            {visible.map((it) => (
              <li
                key={it.file_id}
                className="flex flex-col gap-2 rounded-lg border border-amber-500/30 bg-background/80 px-3 py-2 sm:flex-row sm:items-center"
                data-testid={`drawing-alert-${it.file_id}`}
              >
                <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 sm:flex">
                  {it.mime_type === "application/pdf" ? <FileText className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium" data-testid={`drawing-alert-name-${it.file_id}`}>
                    {it.filename}
                  </p>
                  <p className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted-foreground">
                    <span className="font-semibold text-foreground">{it.revision ? `Rev ${it.revision}` : `v${it.version_number}`}</span>
                    <span>replaces {it.previous_revision ? `Rev ${it.previous_revision}` : `v${it.previous_version_number}`}</span>
                    {!jobId && it.job_name && <span>· {it.job_name}</span>}
                    <span>· issued {fmtDate(it.issued_at)}</span>
                    {it.label && <span>· {it.label}</span>}
                  </p>
                  {it.change_note && (
                    <p className="mt-0.5 truncate text-[11px] text-muted-foreground" title={it.change_note} data-testid={`drawing-alert-note-${it.file_id}`}>
                      <span className="font-semibold">What changed:</span> {it.change_note}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  className="h-9 shrink-0 bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400 sm:h-8"
                  onClick={() => open(it)}
                  data-testid={`drawing-alert-open-${it.file_id}`}
                >
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open {it.revision ? `Rev ${it.revision}` : "current"}
                </Button>
              </li>
            ))}
          </ul>
          {more > 0 && (
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-900 underline-offset-2 hover:underline dark:text-amber-100"
              onClick={() => setExpanded(true)}
              data-testid="drawing-alerts-show-more"
            >
              <ChevronDown className="h-3.5 w-3.5" /> Show {more} more
            </button>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-amber-900/70 hover:bg-amber-500/15 dark:text-amber-100/70"
          onClick={() => setHiddenThisSession(true)}
          aria-label="Hide for now (comes back next visit)"
          title="Hide for now — it comes back next visit until you've opened the drawings"
          data-testid="drawing-alerts-hide"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </section>
  );
};
